import React from 'react';
import type { VoiceAnalysis } from '@/api/voice';
import { Button } from '@/ui';
import AIDraftBadge from './AIDraftBadge';

interface VoiceAnalysisCardProps {
  analysis: VoiceAnalysis;
  /** Display name for the room the drafts landed in. Surfaces in the title and primary CTA. */
  roomName?: string;
  /** Tap "Review in <Room>" — dismisses the sheet so the inspector can act on the
   *  per-draft Accept / Edit / Ignore controls in the room's findings list. */
  onReview: () => void;
  /** Tap "Discard memo" — deletes every draft this analysis created. */
  onDiscard: () => void;
}

/**
 * Slim post-recording confirmation (Decision A1). The drafts are already in
 * the room as AI-draft findings with their own per-card Accept / Edit / Ignore;
 * this sheet just announces and routes. Inline review (the old A2 modal with
 * checkboxes and bulk add) was deliberately dropped to avoid two reviewable
 * surfaces for the same drafts.
 */
const VoiceAnalysisCard: React.FC<VoiceAnalysisCardProps> = ({
  analysis,
  roomName,
  onReview,
  onDiscard,
}) => {
  const draftCount = analysis.findingIds?.length ?? 0;
  const inLocation = roomName ? ` in ${roomName}` : '';
  const reviewLabel = roomName ? `Review in ${roomName}` : 'Review drafts';

  if (draftCount === 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <AIDraftBadge />
          <span className="text-caption text-ink-faint">voice memo</span>
        </div>
        <p className="text-body-sm text-ink-muted leading-relaxed">
          Nothing actionable came out of this memo. The transcript is logged but no drafts were created.
        </p>
        <Button variant="secondary" onClick={onDiscard} className="w-full">
          Done
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <AIDraftBadge />
        <span className="text-caption text-ink-faint">voice memo</span>
      </div>
      <h4 className="text-heading-sm text-ink leading-snug">
        {draftCount} draft{draftCount !== 1 ? 's' : ''} created{inLocation}
      </h4>
      <p className="text-body-sm text-ink-muted leading-relaxed">
        Review and tap Accept on each below — they're highlighted in the room. Nothing has been saved to the report yet.
      </p>
      <div className="flex gap-2 pt-1">
        <Button variant="secondary" onClick={onDiscard} className="flex-1">
          Discard memo
        </Button>
        <Button onClick={onReview} className="flex-1">
          {reviewLabel}
          <svg className="w-3.5 h-3.5 ml-1" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" d="m9 18 6-6-6-6" />
          </svg>
        </Button>
      </div>
    </div>
  );
};

export default VoiceAnalysisCard;
